/**
 * Vocabulary: readers see this feature as "owner reviews"; code, routes, DealHunt paths and
 * analytics events keep the historical "owner note" name on purpose — do not rename them.
 *
 * The review's "What owners say" card (design 2026-10-29): the owner rating (`OwnerRating`, shared
 * with the header strip) as a row over the reviewed product's `top_notes` one per slide, in full
 * (the strip shows the clamped preview), each with up to `MAX_CHIPS` reads where that owner
 * departed from expectation; under them "Against what owners expected",
 * the reads' counts as bars plus what gave out and who still uses it daily; then the ask.
 * Same rules as the other owner carousels: stored order (#54), every slide in the server HTML
 * and the controls only scroll (`note-carousel.ts`), a slide on screen is a read of that note
 * (`useOwnerViewBeacon`, #138). Every link goes to the product page — the review → PDP links
 * the 2026-09-23 SEO audit found missing — because the Owners section there carries the full
 * notes. With the bot check configured the ask opens the owner form in place
 * (`ReviewOwnerBand`), else it links the product page's Owners section.
 *
 * Events are `review_page_card_click` with `list_name: review_owners`; `location: ask` is
 * the ask's "Add your owner review".
 */
import { type MouseEvent, useRef } from 'react'
import clsx from 'clsx'
import styles from './ReviewOwnersCarousel.module.css'
import OwnerHint from '../OwnerHint'
import { ReadBar, ReadLegend } from '../ReadBar'
import ReviewOwnerBand from './ReviewOwnerBand'
import { OwnerRating } from './ReviewOwnerRating'
import MemberName from 'components/community/MemberName'
import { AvatarPhoto } from 'components/community/TierAvatar'
import { avatarInitials } from 'lib/community/model'
import { useCatalogEvent } from 'lib/analytics/catalog'
import type { GuidePickContext, OwnerAggregate } from 'lib/dealhunt/types'
import { reviewOwnerScope, useOwnerViewBeacon } from 'lib/owner-notes/analytics'
import {
  GATED_REVIEW_ASK_COPY,
  noteDeepLink,
  noteOwnedLine,
  ownersDeepLink,
  REVIEW_ASK_COPY,
} from 'lib/owner-notes/band-copy'
import { signInGateFor } from 'lib/owner-notes/signin-gate'
import { noteReads, readVerdict, type CardOwnersView } from 'lib/owner-notes/card-owners'
import { gaveOutCallout, readSplit } from 'lib/owner-notes/owner-figures'
import { PHOTO_HINT } from 'lib/owner-notes/hints'
import { type OwnerBandOrigin, requestOpenOwnerBand } from 'lib/owner-notes/bus'
import { useNoteCarousel } from 'lib/owner-notes/note-carousel'
import { turnstileConfigured } from 'lib/owner-notes/turnstile'

/** Departure chips per slide; the rest are counted ("+3 more") and all of them are in
 *  "Against what owners expected" below. More chips made each product's card a different
 *  height. */
const MAX_CHIPS = 2

/** One click event for every review → PDP owner link, the carousel's and the strip's alike. */
export function useReviewOwnersClick(
  context: GuidePickContext
): (location: 'note' | 'owners' | 'ask', destination: string) => void {
  const { track } = useCatalogEvent()
  return (location, destination) =>
    track('review_page_card_click', {
      ...reviewOwnerScope(context, 'review_owners'),
      location,
      destination_url: destination,
    })
}

/** An ask's click: open the page's owner form in place; without the bot check, follow the link. */
export function openReviewBand(e: MouseEvent, origin: OwnerBandOrigin): void {
  if (turnstileConfigured()) {
    e.preventDefault()
    requestOpenOwnerBand(origin)
  }
}

interface ReviewOwnersCarouselProps {
  context: GuidePickContext
  /** Null (no published notes) renders the ask alone. */
  owners: CardOwnersView | null
  /** The product page's aggregate (`enrichment.owners`), for what gave out and who still uses
   *  it daily; the reads come from `owners`. Null or absent leaves those two lines out. */
  aggregate?: OwnerAggregate | null
  className?: string
}

const ReviewOwnersCarousel: React.VFC<ReviewOwnersCarouselProps> = ({
  context,
  owners,
  aggregate = null,
  className,
}) => {
  const onPdpClick = useReviewOwnersClick(context)
  const notes = owners?.notes ?? []
  const { trackRef, active, goTo } = useNoteCarousel(notes.length)
  const rootRef = useRef<HTMLElement>(null)
  useOwnerViewBeacon(rootRef, { enabled: notes.length > 0, deps: [notes.length] })

  const many = notes.length > 1
  const ownersHref = ownersDeepLink(context.sg_href)
  const reads = owners?.reads.filter(r => r.answers > 0) ?? []
  const gaveOut = aggregate ? gaveOutCallout(aggregate) : null
  const daily = aggregate?.still_using?.find(o => o.id === 'yes')

  return (
    <section
      ref={rootRef}
      className={clsx(styles.root, className)}
      aria-label={`Owner reviews of the ${context.name}`}
    >
      <div className={styles.card}>
        {owners && notes.length > 0 ? (
          <>
            <div className={styles.top}>
              <OwnerRating owners={owners} layout="row" />
              <div className={styles.reviews}>
                <div className={styles.head}>
                  <span className={styles.position}>
                    {`Owner review ${active + 1} of ${notes.length}`}
                  </span>
                  {many && (
                    <div className={styles.arrows}>
                      {([-1, 1] as const).map(step => (
                        <button
                          key={step}
                          type="button"
                          className={styles.arrow}
                          aria-label={`${step < 0 ? 'Previous' : 'Next'} owner review`}
                          disabled={active + step < 0 || active + step >= notes.length}
                          onClick={() => goTo(active + step)}
                        >
                          {step < 0 ? '‹' : '›'}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div className={styles.track} ref={trackRef}>
                  {notes.map((note, i) => {
                    const owned = noteOwnedLine(note)
                    const noteHref = noteDeepLink(context.sg_href, note.id)
                    const departures = noteReads(owners, note).filter(r => r.value !== 'expected')
                    return (
                      <figure
                        key={note.id}
                        className={styles.slide}
                        data-note-id={note.id}
                        role={many ? 'group' : undefined}
                        aria-label={many ? `Owner review ${i + 1} of ${notes.length}` : undefined}
                      >
                        <blockquote className={styles.quote}>{`“${note.note}”`}</blockquote>
                        <a
                          href={noteHref}
                          className={styles.noteLink}
                          onClick={() => onPdpClick('note', noteHref)}
                        >
                          {'Read full review →'}
                        </a>
                        {departures.length > 0 && (
                          <ul className={styles.chips}>
                            {departures.slice(0, MAX_CHIPS).map(read => (
                              <li
                                key={read.id}
                                className={clsx(
                                  styles.chip,
                                  read.value === 'worse' ? styles.chipWorse : styles.chipBetter
                                )}
                              >
                                {`${read.label}: ${readVerdict(read.value)}`}
                              </li>
                            ))}
                            {departures.length > MAX_CHIPS && (
                              <li className={styles.more}>
                                {`+${departures.length - MAX_CHIPS} more`}
                              </li>
                            )}
                          </ul>
                        )}
                        <figcaption className={styles.meta}>
                          {/* DealHunt #218: photo over the initials when the member shows one. */}
                          <span className={styles.avatar} aria-hidden="true">
                            {avatarInitials(note.handle, note.display_name)}
                            {note.avatar_url && (
                              <AvatarPhoto key={note.avatar_url} src={note.avatar_url} px={34} />
                            )}
                          </span>
                          <OwnerHint hint={PHOTO_HINT[note.photo_status]} className={styles.hint}>
                            <span className={styles.handle}>
                              <MemberName name={note.display_name} handle={note.handle} />
                            </span>
                          </OwnerHint>
                          {note.photo_status === 'verified' && (
                            <span className={styles.verified}>{'Photo-verified'}</span>
                          )}
                          {owned && <span className={styles.owned}>{owned}</span>}
                        </figcaption>
                      </figure>
                    )
                  })}
                </div>

                {many && (
                  <div className={styles.dots} role="tablist" aria-label="Owner review">
                    {notes.map((note, i) => (
                      <button
                        key={note.id}
                        type="button"
                        role="tab"
                        className={styles.dot}
                        aria-selected={i === active}
                        aria-label={`Owner review ${i + 1} of ${notes.length}`}
                        onClick={() => goTo(i)}
                      >
                        <span className={styles.dotMark} aria-hidden="true" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {reads.length > 0 && (
              <div className={styles.expect}>
                <div className={styles.expectHead}>
                  <h3 className={styles.expectTitle}>{'Against what owners expected'}</h3>
                  <span className={styles.expectSub}>
                    {'Owner-reported after real use, not measured'}
                  </span>
                  <ReadLegend className={styles.legend} />
                </div>
                <ul className={styles.reads} aria-label="How it compared with expectations">
                  {reads.map(read => (
                    <li key={read.id} className={styles.read}>
                      <span className={styles.readLabel}>{read.label}</span>
                      <span className={styles.readSplit}>{readSplit(read, false)}</span>
                      <ReadBar read={read} />
                    </li>
                  ))}
                  {gaveOut || daily ? (
                    <li className={styles.facts}>
                      {gaveOut && (
                        <span>
                          {'What gave out first: '}
                          <strong>{gaveOut.text}</strong>
                        </span>
                      )}
                      {daily && aggregate && (
                        <span>
                          {'Still in daily use: '}
                          <strong>{`${daily.count} of ${aggregate.published_count}`}</strong>
                        </span>
                      )}
                    </li>
                  ) : null}
                </ul>
              </div>
            )}
          </>
        ) : null}

        <div className={styles.askRow}>
          <div className={styles.askText}>
            <p className={styles.askTitle}>{`Own the ${context.name}?`}</p>
            <p className={styles.askSub}>
              {(signInGateFor('review') ? GATED_REVIEW_ASK_COPY : REVIEW_ASK_COPY).carouselSub}
              <span className={styles.askMore}>
                {
                  ' Your answers feed the summary above, and a photo of your pair earns the photo-verified tag.'
                }
              </span>
            </p>
          </div>
          <div className={styles.askBtns}>
            <a
              href={ownersHref}
              className={clsx(styles.btn, styles.primary)}
              onClick={e => {
                onPdpClick('ask', ownersHref)
                openReviewBand(e, 'section')
              }}
            >
              {'Add your owner review →'}
            </a>
            {owners && notes.length > 0 ? (
              // Keep this button: the product page lists every owner review in one place, an
              // easier read than paging through them one slide at a time here.
              <a
                href={ownersHref}
                className={clsx(styles.btn, styles.secondary)}
                onClick={() => onPdpClick('owners', ownersHref)}
              >
                {`See all ${owners.count} owner review${owners.count === 1 ? '' : 's'}`}
              </a>
            ) : null}
          </div>
        </div>
      </div>
      <ReviewOwnerBand context={context} className={styles.band} />
    </section>
  )
}
export default ReviewOwnersCarousel
